"use client";

import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Badge } from "../data-display/badge";
import { Notice } from "../feedback/notice";
import { Button } from "../inputs/button";
import { Input } from "../inputs/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../primitives/select";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../primitives/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../primitives/popover";
import { Toggle } from "../primitives/toggle";
import { useLinkComponent } from "../navigation/link-context";
import { useContainerSize } from "../hooks/use-container-size";
import { TruncatedValue } from "../data-display/truncated-value";
import { cn } from "../utils";
import { ComboboxMultiFilter, ToggleFilter, type FilterOption } from "./toolbar-filter";
import {
  shortcutKey,
  useKeyboardShortcutRegistration,
  type KeyboardShortcutCommand,
} from "../keyboard-shortcuts/keyboard-shortcuts";
import {
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight,
  Settings2,
  SlidersHorizontal,
  ListFilter,
  Check,
  X,
} from "lucide-react";

/** Configuration for inline editing */
export interface EditableConfig {
  /** Type of editor to use */
  type: "text" | "number" | "select";
  /** Options for select type */
  options?: { label: string; value: string }[];
  /** Validation function - return error message or undefined if valid */
  validate?: (value: unknown) => string | undefined;
  /** Placeholder text for input */
  placeholder?: string;
}

export interface DataTableColumn<T = unknown> {
  /**
   * Column key. Supports nested properties using dot notation (e.g., "user.name").
   * Can be a key of T or a string for nested/computed properties.
   */
  key: keyof T | string;
  /** Column header label */
  label: string;
  /** Whether this column can be sorted (requires enableSorting on table) */
  sortable?: boolean;
  /**
   * Optional accessor that returns the value to sort by for this column.
   * Use this when `key` is a synthetic/dot-path key whose nested target may be null,
   * or when the displayed cell differs from the natural sort value.
   * Falls back to `getValue(row, key)` (which supports dot-notation) when omitted.
   */
  sortValue?: (row: T) => unknown;
  /**
   * Custom render function for cell content.
   * @param value - The cell value (type depends on the column key)
   * @param row - The full row data
   * @param index - The row index
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  render?: (value: any, row: T, index: number) => React.ReactNode;
  /** Additional CSS classes for the column */
  className?: string;
  /** Fixed width for the column. Use `px` or `rem` — see `minWidth`. */
  width?: string;
  /**
   * Minimum width applied to the column's `<th>`/`<td>`. Also contributes to
   * the table's intrinsic width under `table-fixed`, which is what lets the
   * table scroll horizontally instead of crushing columns. Only `px` and `rem`
   * contribute; container-relative lengths (`%`, `vw`, `auto`) still apply as
   * CSS but count as 0 toward that width.
   *
   * Declaring neither this nor `width` opts the column into
   * `DEFAULT_COLUMN_MIN_WIDTH`. Pass `minWidth: "0"` for a column that may
   * compress below it.
   */
  minWidth?: string;
  /** Opt out of default truncation — cell keeps `whitespace-nowrap` and does not ellipsize. String cells are not auto-wrapped. */
  nowrap?: boolean;
  /**
   * Hide this column in table view when the table's container is narrower
   * than this many pixels. Lets a table shed low-priority columns
   * progressively as space shrinks, before the card view takes over at
   * `cardBreakpoint`. Measured against the DataTable's own container (via
   * ResizeObserver), not the viewport — a table in a narrow pane collapses
   * the same way as one on a narrow screen.
   */
  hideBelow?: number;
  /** Hide this column in the narrow-container card view */
  mobileHidden?: boolean;
  /** Use this column as the card title in the card view (defaults to first column) */
  mobilePrimary?: boolean;
  /**
   * Render this column in the card view as muted body text under the card
   * title instead of a label/value row. For prose-like cells (descriptions,
   * summaries) whose label would be noise on a card.
   */
  mobileSecondary?: boolean;
  /** Whether this column can be hidden via column visibility toggle (default: true) */
  hideable?: boolean;
  /**
   * Keep `label` for assistive tech but hide it visually — an actions column
   * whose header would otherwise be blank (axe `empty-table-header`).
   */
  labelHidden?: boolean;
  /** Configuration for inline editing - if provided, column is editable */
  editable?: EditableConfig;
}

export type SortDirection = "asc" | "desc" | null;

export interface SortState {
  column: string | null;
  direction: SortDirection;
}

export interface PaginationState {
  pageIndex: number;
  pageSize: number;
}

export type ColumnVisibility = Record<string, boolean>;

export interface DataTableKeyboardSelection {
  selectedRowKeys: ReadonlySet<React.Key>;
  onSelectedRowKeysChange: (selectedRowKeys: Set<React.Key>) => void;
}

export interface DataTableKeyboardNavigation<T> {
  /** Opens the active row when Enter is pressed. */
  onOpenRow?: (row: T, index: number) => void;
  /** Enables x to toggle the active row for tables with bulk actions. */
  selection?: DataTableKeyboardSelection;
  /** Focuses this list's search control when / is pressed. */
  focusSearch?: () => void;
  /** Refreshes this list when r is pressed. */
  onRefresh?: () => void;
  /** Suppresses refresh while the backing query is already fetching. */
  refreshDisabled?: boolean;
}

/**
 * Cursor-based pagination props. When provided, DataTable renders Prev/Next
 * buttons in the footer (in addition to / instead of offset pagination).
 * Pair this with the `useCursorPager` hook from `@quovy/ui`.
 */
export interface CursorPaginationProps {
  /** Whether a next page is available (typically `Boolean(data.next_cursor)`). */
  hasNext: boolean;
  /** Whether a previous page is available (from `useCursorPager().hasPrev`). */
  hasPrev: boolean;
  /** Advance to the next page. */
  onNext: () => void;
  /** Return to the previous page. */
  onPrev: () => void;
  /** When true, disables both buttons (e.g. while the query is fetching). */
  isLoading?: boolean;
}

/** Edit state for tracking which cell is being edited */
export interface EditState {
  rowIndex: number;
  columnKey: string;
  value: unknown;
  error: string | undefined;
}

/**
 * Declarative filter definition for a column. Five variants:
 *
 *   - **Options** (default; the discriminator is optional): a popover with a
 *     multi-select list of enumerated options. The classic case.
 *   - **Single**: the same popover, but picking an option replaces the
 *     selection instead of adding to it. Use when the backing query param
 *     cannot express a union (e.g. a boolean facet, where selecting both
 *     values is a contradiction).
 *   - **Text**: a free-text `<Input>` for substring matching. Use for
 *     path / name / id fragments that don't fit an enum.
 *   - **Number**: a numeric `<Input>` for exact-match status codes / counts.
 *   - **Toggle**: an on/off button backed by one active value.
 *
 * For text/number, the value is stored in `FilterValues` as a single-element
 * array (empty array = no filter active). Consumers using `manualFiltering`
 * read `filterValues[key]?.[0]` and pass it to their query.
 */
export type DataTableFilter =
  | DataTableOptionsFilter
  | DataTableSingleFilter
  | DataTableTextFilter
  | DataTableNumberFilter
  | DataTableToggleFilter;

export interface DataTableOptionsFilter {
  key: string;
  label: string;
  /** Optional discriminator; omitted is treated as `"options"`. */
  kind?: "options";
  options: FilterOption[];
  searchPlaceholder?: string;
}

/** Single-select. Still stores `string[]` (0 or 1 member) so `FilterValues` is one shape. */
export interface DataTableSingleFilter {
  key: string;
  label: string;
  kind: "single";
  options: FilterOption[];
  searchPlaceholder?: string;
}

export interface DataTableTextFilter {
  key: string;
  label: string;
  kind: "text";
  placeholder?: string;
  /** Debounce keystrokes (ms) before firing `onFilterChange`. Default 0. */
  debounceMs?: number;
  /** Override the default width / styling. */
  inputClassName?: string;
}

export interface DataTableNumberFilter {
  key: string;
  label: string;
  kind: "number";
  placeholder?: string;
  debounceMs?: number;
  inputClassName?: string;
}

export interface DataTableToggleFilter {
  key: string;
  label: string;
  kind: "toggle";
  /** Value stored in `FilterValues` while the toggle is pressed. */
  activeValue: string;
  icon?: React.ReactNode;
}

/** Map of filter key → selected values (single-element array for text/number). */
export type FilterValues = Record<string, string[]>;

export interface DataTableProps<T> {
  data: T[];
  columns: DataTableColumn<T>[];
  loading?: boolean;
  emptyState?: React.ReactNode;
  /** `event` is absent when the row is opened via keyboard (`onOpenRow` fallback). */
  onRowClick?: (row: T, index: number, event?: React.MouseEvent) => void;
  /**
   * Renders detail content below a row. Adds an accessible disclosure control
   * in both table and narrow-card views.
   */
  renderExpandedRow?: (row: T, index: number) => React.ReactNode;
  expandedRowKeys?: Set<React.Key>;
  onExpandedRowKeysChange?: (keys: Set<React.Key>) => void;
  className?: string;
  /** Extra classes applied to the underlying <table> element (e.g. "w-full table-fixed"). */
  tableClassName?: string;
  /** Hide the column header row visually; screen readers still announce column names. */
  hideHeader?: boolean;
  /**
   * Override `DEFAULT_COLUMN_MIN_WIDTH` for columns that declare no
   * `width`/`minWidth` of their own. Use `"0"` for a table that should always
   * fit its container rather than scroll.
   */
  defaultColumnMinWidth?: string;
  rowClassName?: (row: T, index: number) => string;
  /** Content rendered above the table (filters, actions) */
  toolbar?: React.ReactNode;
  /** Content rendered below the table (counts, summaries) */
  footer?: React.ReactNode;
  /** Enable sorting functionality. When true, columns with sortable=true can be sorted. */
  enableSorting?: boolean;
  /**
   * When true, render sort controls (headers, icons, `onSortChange`) without
   * reordering rows locally — the data arrives already sorted by the server.
   * Mirrors `manualFiltering`. Pair with controlled `sort` + `onSortChange`.
   */
  manualSorting?: boolean;
  /** Initial sort state */
  defaultSort?: SortState;
  /** Controlled sort state */
  sort?: SortState;
  /** Callback when sort changes (for controlled mode) */
  onSortChange?: (sort: SortState) => void;
  /** Enable offset/page-number pagination. Mutually exclusive with `cursorPagination`. */
  enablePagination?: boolean;
  /**
   * Enable cursor-based pagination. When set, renders Prev/Next buttons in the
   * footer using the same styling as offset pagination. Designed to pair with
   * the `useCursorPager` hook.
   */
  cursorPagination?: CursorPaginationProps;
  /** Available page sizes */
  pageSizeOptions?: number[];
  /** Default page size */
  defaultPageSize?: number;
  /** Controlled pagination state */
  pagination?: PaginationState;
  /** Callback when pagination changes (for controlled mode) */
  onPaginationChange?: (pagination: PaginationState) => void;
  /** Total item count for server-side pagination. When set, disables client-side slicing. */
  totalItems?: number;
  /** Enable column visibility toggle */
  enableColumnVisibility?: boolean;
  /**
   * Render DataTable's own internal column-visibility toggle. Defaults to true.
   * Set false to keep column filtering working (driven by
   * `enableColumnVisibility` + `columnVisibility`) while suppressing the
   * built-in control — e.g. when rendering a `DataTableColumnVisibilityToggle`
   * elsewhere in the page.
   */
  showColumnVisibilityToggle?: boolean;
  /**
   * Linear-style Filter + Display controls on the toolbar, instead of the
   * labeled Columns button. Filter only appears when `filters` is set.
   * Display only appears when column visibility is enabled.
   */
  viewControls?: boolean;
  /** Default column visibility state (columns hidden by default) */
  defaultColumnVisibility?: ColumnVisibility;
  /** Controlled column visibility state */
  columnVisibility?: ColumnVisibility;
  /** Callback when column visibility changes (for controlled mode) */
  onColumnVisibilityChange?: (visibility: ColumnVisibility) => void;
  /** Callback when a cell value is edited. Required if any columns have editable config. */
  onCellEdit?: (
    rowIndex: number,
    columnKey: string,
    value: unknown,
    row: T,
  ) => void | Promise<void>;
  /** Callback when a cell edit fails. Receives the error for custom handling/logging. */
  onCellEditError?: (
    error: Error,
    rowIndex: number,
    columnKey: string,
    value: unknown,
    row: T,
  ) => void;
  /** Declarative filter definitions. Renders ComboboxMultiFilter in toolbar and applies client-side filtering. */
  filters?: DataTableFilter[];
  /** When true, render filter controls without filtering rows locally. */
  manualFiltering?: boolean;
  /** Controlled filter state: { [filterKey]: selectedValues[] }. When omitted, internal state is used. */
  filterValues?: FilterValues;
  /** Callback when filter selection changes (for URL deep-linking or controlled mode). */
  onFilterChange?: (values: FilterValues) => void;
  /**
   * Extract a stable React key for each row. Defaults to the row index, which is
   * fine for read-only tables but breaks reconciliation for editable/reorderable
   * rows (focus loss, stale inputs). Pass a function that returns a unique id.
   */
  getRowKey?: (row: T, index: number) => string | number;
  /**
   * Container width (px) below which rows render as stacked cards instead of a
   * table. Measured against the DataTable's own container, not the viewport,
   * so tables inside narrow panes/drawers collapse too. Default 640.
   */
  cardBreakpoint?: number;
  /** Gmail-style j/k row navigation with Enter, optional x selection, and ? help. Requires getRowKey. */
  keyboardNavigation?: DataTableKeyboardNavigation<T> | false;
}

/** Root font size, in px. No stylesheet overrides `html { font-size }`. */
const ROOT_FONT_SIZE_PX = 16;

/**
 * Width every column reserves toward the table's min-width unless it says
 * otherwise. Below roughly this, a cell is ellipsis rather than content, so
 * the useful failure mode for a table with more columns than room is to
 * scroll — not to compress all of them past reading. Override per table with
 * `defaultColumnMinWidth`, or per column with an explicit `width`/`minWidth`.
 */
export const DEFAULT_COLUMN_MIN_WIDTH = "120px";

/**
 * Resolve a CSS length string to px so column widths can be summed into a
 * table-level min-width.
 *
 * Only absolute lengths are resolvable: `px` and `rem`. Everything else —
 * percentages, `em`, viewport units, `auto`, `calc()`, unitless numbers —
 * returns 0, because a container-relative length cannot contribute a fixed
 * number to the sum. Contributing 0 is the safe direction: the column still
 * gets its CSS `min-width`, the table just doesn't count it toward the width
 * at which it starts scrolling.
 *
 * Do not loosen this to a bare `parseFloat` — that silently read "11rem" as
 * 11px, which is a wrong number rather than a missing one.
 */
function cssLengthToPx(v?: string): number {
  if (!v) return 0;
  const match = /^\s*(-?\d*\.?\d+)(px|rem)\s*$/.exec(v);
  if (!match) return 0;
  const n = Number.parseFloat(match[1]!);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return match[2] === "rem" ? n * ROOT_FONT_SIZE_PX : n;
}

/**
 * What one column contributes to the table's min-width. An explicit `width` or
 * `minWidth` is authoritative even when it resolves to 0 — `"50%"`, `"auto"`
 * and `"0"` are all statements that this column sizes itself, not omissions —
 * so only a column that declares neither takes the default.
 */
function columnMinWidthPx(col: { width?: string; minWidth?: string }, defaultPx: number): number {
  if (col.width != null || col.minWidth != null) {
    return cssLengthToPx(col.width) || cssLengthToPx(col.minWidth);
  }
  return defaultPx;
}

export interface DataTableColumnVisibilityToggleProps<T = unknown> {
  columns: DataTableColumn<T>[];
  columnVisibility: ColumnVisibility;
  onColumnVisibilityChange: (next: ColumnVisibility) => void;
  align?: "start" | "end";
  className?: string;
}

/**
 * Standalone "Columns" visibility dropdown, extracted from `DataTable`'s
 * internal toolbar so it can be rendered anywhere (e.g. next to page-level
 * action buttons). Renders identical markup/behavior to the built-in toggle.
 * Returns null when there are no hideable columns. Pair with DataTable's
 * `showColumnVisibilityToggle={false}` + controlled `columnVisibility`.
 */
export function DataTableColumnVisibilityToggle<T = unknown>({
  columns,
  columnVisibility,
  onColumnVisibilityChange,
  align = "end",
  className,
}: DataTableColumnVisibilityToggleProps<T>) {
  const hideableColumns = columns.filter((col) => col.hideable !== false && col.label);
  if (hideableColumns.length === 0) return null;

  const toggle = (key: string) => {
    onColumnVisibilityChange({
      ...columnVisibility,
      [key]: columnVisibility[key] === false ? true : false,
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className={cn("h-8", className)}>
          <Settings2 className="h-4 w-4 mr-2" />
          Columns
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-48">
        <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {hideableColumns.map((column) => (
          <DropdownMenuCheckboxItem
            key={String(column.key)}
            checked={columnVisibility[String(column.key)] !== false}
            onCheckedChange={() => toggle(String(column.key))}
          >
            {column.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function hideableColumnsOf<T>(columns: DataTableColumn<T>[]): DataTableColumn<T>[] {
  return columns.filter((col) => col.hideable !== false && col.label);
}

function toggleColumnVisibility(columnVisibility: ColumnVisibility, key: string): ColumnVisibility {
  return {
    ...columnVisibility,
    [key]: columnVisibility[key] === false ? true : false,
  };
}

/**
 * Linear-style Filter + Display strip. Display opens a property-chip
 * popover; Filter wraps existing `DataTable.filters` so they don't sit
 * inline. Either control can be omitted.
 */
export function DataTableViewControls<T = unknown>({
  columns,
  columnVisibility,
  onColumnVisibilityChange,
  showDisplay,
  showFilter,
  filterContent,
  activeFilterCount = 0,
}: {
  columns: DataTableColumn<T>[];
  columnVisibility: ColumnVisibility;
  onColumnVisibilityChange: (next: ColumnVisibility) => void;
  showDisplay?: boolean;
  showFilter?: boolean;
  filterContent?: React.ReactNode;
  activeFilterCount?: number;
}) {
  const hideableColumns = hideableColumnsOf(columns);
  const visibleCount = hideableColumns.filter(
    (col) => columnVisibility[String(col.key)] !== false,
  ).length;

  if (!showDisplay && !showFilter) return null;

  return (
    <div className="flex items-center gap-0.5">
      {showFilter ? (
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 text-muted-foreground">
              <ListFilter className="size-3.5" />
              Filter
              {activeFilterCount > 0 ? (
                <span className="tabular-nums text-foreground">{activeFilterCount}</span>
              ) : null}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-72 space-y-2 p-3">
            {filterContent}
          </PopoverContent>
        </Popover>
      ) : null}
      {showDisplay && hideableColumns.length > 0 ? (
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 text-muted-foreground">
              <SlidersHorizontal className="size-3.5" />
              Display
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 p-3">
            <p className="mb-2 text-xs font-medium text-muted-foreground">Display properties</p>
            <div className="flex flex-wrap gap-1.5">
              {hideableColumns.map((column) => {
                const key = String(column.key);
                const on = columnVisibility[key] !== false;
                return (
                  <Toggle
                    key={key}
                    size="sm"
                    variant="outline"
                    pressed={on}
                    aria-label={`${on ? "Hide" : "Show"} ${column.label}`}
                    className="h-7 min-w-0 max-w-full rounded-full px-2.5 text-xs"
                    disabled={on && visibleCount <= 1}
                    onPressedChange={() =>
                      onColumnVisibilityChange(toggleColumnVisibility(columnVisibility, key))
                    }
                  >
                    <TruncatedValue className="max-w-56">{column.label}</TruncatedValue>
                  </Toggle>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
      ) : null}
    </div>
  );
}

export function DataTable<T extends object>({
  data,
  columns,
  loading = false,
  emptyState,
  onRowClick,
  renderExpandedRow,
  expandedRowKeys: controlledExpandedRowKeys,
  onExpandedRowKeysChange,
  className = "",
  tableClassName = "",
  hideHeader = false,
  defaultColumnMinWidth = DEFAULT_COLUMN_MIN_WIDTH,
  rowClassName,
  enableSorting = false,
  manualSorting = false,
  defaultSort,
  sort: controlledSort,
  onSortChange,
  enablePagination = false,
  cursorPagination,
  pageSizeOptions = [10, 20, 50, 100],
  defaultPageSize = 10,
  pagination: controlledPagination,
  onPaginationChange,
  totalItems,
  enableColumnVisibility = false,
  showColumnVisibilityToggle = true,
  viewControls = false,
  defaultColumnVisibility,
  columnVisibility: controlledColumnVisibility,
  onColumnVisibilityChange,
  onCellEdit,
  onCellEditError,
  filters,
  manualFiltering = false,
  filterValues: controlledFilterValues,
  onFilterChange,
  toolbar,
  footer,
  getRowKey,
  cardBreakpoint = 640,
  keyboardNavigation,
}: DataTableProps<T>) {
  // Responsive mode is driven by the table's own container width, not the
  // viewport: a table beside a sidebar or inside a drawer sees its actual
  // available space. Until the first real measurement lands, render the full
  // table so wide layouts never flash the card view. A zero width means the
  // container isn't rendered yet (hidden tab panel, display:none ancestor) —
  // treat it as unmeasured, not narrow.
  const { containerRef, containerSize } = useContainerSize<HTMLDivElement>();
  const containerWidth = containerSize && containerSize.width > 0 ? containerSize.width : undefined;
  const isCardView = containerWidth != null && containerWidth < cardBreakpoint;
  const [internalSort, setInternalSort] = useState<SortState>(
    defaultSort || { column: null, direction: null },
  );
  const [internalPagination, setInternalPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });
  const [internalColumnVisibility, setInternalColumnVisibility] = useState<ColumnVisibility>(
    defaultColumnVisibility || {},
  );
  const [editState, setEditState] = useState<EditState | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [internalFilterValues, setInternalFilterValues] = useState<FilterValues>({});
  const [internalExpandedRowKeys, setExpandedRowKeys] = useState<Set<React.Key>>(() => new Set());
  const expandedRowKeys = controlledExpandedRowKeys ?? internalExpandedRowKeys;
  const expansionIdPrefix = React.useId().replaceAll(":", "");
  const [keyboardActiveRowKey, setKeyboardActiveRowKey] = useState<React.Key | null>(null);
  const keyboardRowRefs = useRef(new Map<React.Key, HTMLElement>());
  const keyboardRootRef = useRef<HTMLDivElement | null>(null);
  const keyboardConfig = keyboardNavigation === false ? undefined : keyboardNavigation;
  const openKeyboardRow = keyboardConfig?.onOpenRow ?? onRowClick;
  const hasExplicitKeyboardConfig = keyboardNavigation != null && keyboardNavigation !== false;
  const keyboardEligible = Boolean(
    getRowKey &&
    keyboardNavigation !== false &&
    (hasExplicitKeyboardConfig ? openKeyboardRow || keyboardConfig?.selection : openKeyboardRow),
  );
  const setContainerRef = useCallback(
    (node: HTMLDivElement | null) => {
      keyboardRootRef.current = node;
      containerRef(node);
    },
    [containerRef],
  );

  // Use controlled state if provided, otherwise use internal state
  const activeFilterValues = controlledFilterValues ?? internalFilterValues;
  const setActiveFilterValues = onFilterChange ?? setInternalFilterValues;
  const activeFilterValuesRef = useRef(activeFilterValues);
  activeFilterValuesRef.current = activeFilterValues;

  const setFilterValue = useCallback(
    (key: string, value: string[]) => {
      const next = { ...activeFilterValuesRef.current, [key]: value };
      // Two independently debounced text filters can fire before their parent
      // applies either controlled update; retain both instead of losing one.
      activeFilterValuesRef.current = next;
      setActiveFilterValues(next);
    },
    [setActiveFilterValues],
  );

  const sort = controlledSort ?? internalSort;
  const setSort = onSortChange ?? setInternalSort;
  const pagination = controlledPagination ?? internalPagination;
  const setPagination = onPaginationChange ?? setInternalPagination;
  const columnVisibility = controlledColumnVisibility ?? internalColumnVisibility;
  const setColumnVisibility = onColumnVisibilityChange ?? setInternalColumnVisibility;

  const getValue = useCallback((row: T, key: keyof T | string): unknown => {
    if (typeof key === "string" && key.includes(".")) {
      // Handle nested properties like "user.name"
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return key.split(".").reduce((obj: any, prop) => obj?.[prop], row);
    }
    return row[key as keyof T];
  }, []);

  const renderCell = useCallback(
    (column: DataTableColumn<T>, row: T, index: number): React.ReactNode => {
      const value = getValue(row, column.key);
      return column.render ? column.render(value, row, index) : (value as React.ReactNode);
    },
    [getValue],
  );

  // Check if any columns are editable
  const hasEditableColumns = useMemo(() => {
    return columns.some((col) => col.editable);
  }, [columns]);

  // Filter visible columns: user toggles first, then responsive `hideBelow`
  // drops columns that don't fit the measured container. The card view skips
  // the `hideBelow` filter — cards have their own `mobileHidden` semantics.
  const visibleColumns = useMemo(() => {
    const base = enableColumnVisibility
      ? columns.filter((col) => columnVisibility[String(col.key)] !== false)
      : columns;
    if (isCardView || containerWidth == null) return base;
    return base.filter((col) => col.hideBelow == null || containerWidth >= col.hideBelow);
  }, [columns, columnVisibility, enableColumnVisibility, isCardView, containerWidth]);

  const columnStyles = useMemo<(React.CSSProperties | undefined)[]>(
    () =>
      visibleColumns.map((col) => {
        if (!col.width && !col.minWidth) return undefined;
        return { width: col.width, minWidth: col.minWidth };
      }),
    [visibleColumns],
  );

  // Sum the per-column widths to derive a table-level min-width. Under
  // `table-fixed` the table is pinned to 100% of its `overflow-x-auto` wrapper,
  // which crushes columns to fit; giving the table an explicit min-width lets it
  // grow past the container and scroll horizontally once the columns' required
  // width exceeds the available space. Columns that declare no width of their
  // own reserve `defaultColumnMinWidth`, so a table with more columns than room
  // scrolls rather than compressing every column into ellipsis.
  const showDisplayControl = viewControls && enableColumnVisibility && showColumnVisibilityToggle;
  const showFilterControl = viewControls && Boolean(filters && filters.length > 0);
  const showLegacyColumnsToggle =
    !viewControls && enableColumnVisibility && showColumnVisibilityToggle;

  const tableMinWidth = useMemo(() => {
    const defaultPx = cssLengthToPx(defaultColumnMinWidth);
    return visibleColumns.reduce((sum, col) => sum + columnMinWidthPx(col, defaultPx), 0);
  }, [visibleColumns, defaultColumnMinWidth]);
  const effectiveTableMinWidth = tableMinWidth + (renderExpandedRow ? 40 : 0);

  // Filter data if filters are active
  const filteredData = useMemo(() => {
    if (!filters || filters.length === 0 || manualFiltering) return data;

    const activeFilters = filters.filter((f) => {
      const vals = activeFilterValues[f.key];
      return vals && vals.length > 0;
    });
    if (activeFilters.length === 0) return data;

    return data.filter((row) =>
      activeFilters.every((filter) => {
        const value = getValue(row, filter.key as keyof T | string);
        const selected = activeFilterValues[filter.key] ?? [];
        const kind = filter.kind ?? "options";
        if (kind === "text") {
          const query = (selected[0] ?? "").toLowerCase();
          if (query === "") return true;
          return String(value ?? "")
            .toLowerCase()
            .includes(query);
        }
        if (kind === "number") {
          const query = selected[0] ?? "";
          if (query === "") return true;
          return String(value ?? "") === query;
        }
        return selected.includes(String(value ?? ""));
      }),
    );
  }, [data, filters, manualFiltering, activeFilterValues, getValue]);

  // Sort data if sorting is enabled
  const sortedData = useMemo(() => {
    if (!enableSorting || manualSorting || !sort.column || !sort.direction) {
      return filteredData;
    }

    const sortColumnDef = columns.find((c) => String(c.key) === sort.column);
    const accessor = sortColumnDef?.sortValue;

    return [...filteredData].sort((a, b) => {
      const aValue = accessor ? accessor(a) : getValue(a, sort.column as keyof T | string);
      const bValue = accessor ? accessor(b) : getValue(b, sort.column as keyof T | string);

      // Handle null/undefined
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sort.direction === "asc" ? 1 : -1;
      if (bValue == null) return sort.direction === "asc" ? -1 : 1;

      // Compare values
      let comparison = 0;
      if (typeof aValue === "string" && typeof bValue === "string") {
        comparison = aValue.localeCompare(bValue);
      } else if (typeof aValue === "number" && typeof bValue === "number") {
        comparison = aValue - bValue;
      } else if (aValue instanceof Date && bValue instanceof Date) {
        comparison = aValue.getTime() - bValue.getTime();
      } else {
        comparison = String(aValue).localeCompare(String(bValue));
      }

      return sort.direction === "asc" ? comparison : -comparison;
    });
  }, [filteredData, sort, enableSorting, manualSorting, getValue, columns]);

  // Server-side pagination: data is already the correct page, skip slicing
  const isServerPaginated = totalItems != null;

  // Paginate data if pagination is enabled (client-side only)
  const paginatedData = useMemo(() => {
    if (!enablePagination || isServerPaginated) {
      return sortedData;
    }

    const start = pagination.pageIndex * pagination.pageSize;
    const end = start + pagination.pageSize;
    return sortedData.slice(start, end);
  }, [sortedData, enablePagination, isServerPaginated, pagination]);

  const rowKey = useCallback(
    (row: T, index: number): React.Key => getRowKey?.(row, index) ?? index,
    [getRowKey],
  );

  const toggleExpandedRow = useCallback(
    (key: React.Key) => {
      const next = new Set(expandedRowKeys);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      if (controlledExpandedRowKeys === undefined) setExpandedRowKeys(next);
      onExpandedRowKeysChange?.(next);
    },
    [expandedRowKeys, controlledExpandedRowKeys, onExpandedRowKeysChange],
  );

  const keyboardActiveIndex = useMemo(
    () =>
      keyboardActiveRowKey == null
        ? -1
        : paginatedData.findIndex((row, index) => rowKey(row, index) === keyboardActiveRowKey),
    [keyboardActiveRowKey, paginatedData, rowKey],
  );

  useEffect(() => {
    if (keyboardActiveRowKey != null && keyboardActiveIndex < 0) {
      setKeyboardActiveRowKey(null);
    }
  }, [keyboardActiveIndex, keyboardActiveRowKey]);

  useEffect(() => {
    if (keyboardActiveRowKey == null) return;
    const node = keyboardRowRefs.current.get(keyboardActiveRowKey);
    node?.focus({ preventScroll: true });
    node?.scrollIntoView?.({ block: "nearest" });
  }, [keyboardActiveRowKey]);

  // Pagination helpers
  const itemCount = isServerPaginated ? totalItems : sortedData.length;
  const totalPages = Math.ceil(itemCount / pagination.pageSize);
  const canPreviousPage = pagination.pageIndex > 0;
  const canNextPage = pagination.pageIndex < totalPages - 1;

  const goToPage = useCallback(
    (pageIndex: number) => {
      setPagination({
        ...pagination,
        pageIndex: Math.max(0, Math.min(pageIndex, Math.max(0, totalPages - 1))),
      });
    },
    [pagination, setPagination, totalPages],
  );

  const moveKeyboardCursor = useCallback(
    (direction: 1 | -1, extendSelection: boolean) => {
      if (paginatedData.length === 0) return;
      const currentIndex = keyboardActiveIndex;
      const nextIndex =
        currentIndex < 0
          ? direction > 0
            ? 0
            : paginatedData.length - 1
          : Math.max(0, Math.min(currentIndex + direction, paginatedData.length - 1));
      const nextKey = rowKey(paginatedData[nextIndex]!, nextIndex);
      if (extendSelection && keyboardConfig?.selection) {
        const selected = new Set(keyboardConfig.selection.selectedRowKeys);
        if (currentIndex >= 0) {
          selected.add(rowKey(paginatedData[currentIndex]!, currentIndex));
        }
        selected.add(nextKey);
        keyboardConfig.selection.onSelectedRowKeysChange(selected);
      }
      setKeyboardActiveRowKey(nextKey);
    },
    [keyboardActiveIndex, keyboardConfig, paginatedData, rowKey],
  );

  const toggleKeyboardSelection = useCallback(() => {
    const selection = keyboardConfig?.selection;
    if (!selection || keyboardActiveIndex < 0) return;
    const key = rowKey(paginatedData[keyboardActiveIndex]!, keyboardActiveIndex);
    const selected = new Set(selection.selectedRowKeys);
    if (selected.has(key)) selected.delete(key);
    else selected.add(key);
    selection.onSelectedRowKeysChange(selected);
  }, [keyboardActiveIndex, keyboardConfig, paginatedData, rowKey]);

  const toggleVisibleKeyboardSelection = useCallback(() => {
    const selection = keyboardConfig?.selection;
    if (!selection || paginatedData.length === 0) return;
    const visibleKeys = paginatedData.map((row, index) => rowKey(row, index));
    const selected = new Set(selection.selectedRowKeys);
    const allVisibleSelected = visibleKeys.every((key) => selected.has(key));
    for (const key of visibleKeys) {
      if (allVisibleSelected) selected.delete(key);
      else selected.add(key);
    }
    selection.onSelectedRowKeysChange(selected);
  }, [keyboardConfig, paginatedData, rowKey]);

  const keyboardCommands = useMemo<KeyboardShortcutCommand[]>(() => {
    const commands: KeyboardShortcutCommand[] = [
      {
        id: "table-next-row",
        description: "Move to next row",
        bindings: [shortcutKey("j")],
        action: () => moveKeyboardCursor(1, false),
        enabled: paginatedData.length > 0,
        allowRepeat: true,
      },
      {
        id: "table-previous-row",
        description: "Move to previous row",
        bindings: [shortcutKey("k")],
        action: () => moveKeyboardCursor(-1, false),
        enabled: paginatedData.length > 0,
        allowRepeat: true,
      },
      {
        id: "table-clear-active-row",
        description: "Clear active row",
        bindings: [shortcutKey("Escape")],
        action: () => setKeyboardActiveRowKey(null),
        enabled: keyboardActiveRowKey != null,
      },
    ];

    if (openKeyboardRow) {
      commands.push({
        id: "table-open-row",
        description: "Open active row",
        bindings: [shortcutKey("Enter"), shortcutKey("o")],
        action: () => {
          if (keyboardActiveIndex >= 0) {
            openKeyboardRow(paginatedData[keyboardActiveIndex]!, keyboardActiveIndex);
          }
        },
        enabled: keyboardActiveIndex >= 0,
      });
    }

    if (keyboardConfig?.selection) {
      commands.push(
        {
          id: "table-toggle-row-selection",
          description: "Select or deselect active row",
          bindings: [shortcutKey("x")],
          action: toggleKeyboardSelection,
          enabled: keyboardActiveIndex >= 0,
        },
        {
          id: "table-extend-selection-next",
          description: "Extend selection to next row",
          bindings: [shortcutKey("j", ["shift"])],
          action: () => moveKeyboardCursor(1, true),
          enabled: paginatedData.length > 0,
          allowRepeat: true,
        },
        {
          id: "table-extend-selection-previous",
          description: "Extend selection to previous row",
          bindings: [shortcutKey("k", ["shift"])],
          action: () => moveKeyboardCursor(-1, true),
          enabled: paginatedData.length > 0,
          allowRepeat: true,
        },
        {
          id: "table-toggle-visible-selection",
          description: "Select or deselect all visible rows",
          bindings: [shortcutKey("x", ["shift"])],
          action: toggleVisibleKeyboardSelection,
          enabled: paginatedData.length > 0,
        },
      );
    }
    if (keyboardConfig?.focusSearch) {
      commands.push({
        id: "table-focus-search",
        description: "Focus search",
        bindings: [shortcutKey("/")],
        action: keyboardConfig.focusSearch,
      });
    }
    if (keyboardConfig?.onRefresh) {
      commands.push({
        id: "table-refresh",
        description: "Refresh list",
        bindings: [shortcutKey("r")],
        action: keyboardConfig.onRefresh,
        enabled: !keyboardConfig.refreshDisabled,
      });
    }
    if (cursorPagination || enablePagination) {
      const previousEnabled = cursorPagination
        ? cursorPagination.hasPrev && !cursorPagination.isLoading
        : canPreviousPage;
      const nextEnabled = cursorPagination
        ? cursorPagination.hasNext && !cursorPagination.isLoading
        : canNextPage;
      commands.push(
        {
          id: "table-previous-page",
          description: "Previous page",
          bindings: [shortcutKey("[")],
          action: () =>
            cursorPagination ? cursorPagination.onPrev() : goToPage(pagination.pageIndex - 1),
          enabled: previousEnabled,
        },
        {
          id: "table-next-page",
          description: "Next page",
          bindings: [shortcutKey("]")],
          action: () =>
            cursorPagination ? cursorPagination.onNext() : goToPage(pagination.pageIndex + 1),
          enabled: nextEnabled,
        },
      );
    }
    return commands;
  }, [
    canNextPage,
    canPreviousPage,
    cursorPagination,
    enablePagination,
    goToPage,
    keyboardActiveIndex,
    keyboardActiveRowKey,
    keyboardConfig,
    moveKeyboardCursor,
    openKeyboardRow,
    paginatedData,
    pagination.pageIndex,
    toggleKeyboardSelection,
    toggleVisibleKeyboardSelection,
  ]);

  const claimKeyboardNavigation = useKeyboardShortcutRegistration({
    scope: "table",
    commands: keyboardCommands,
    active: keyboardEligible,
    elementRef: keyboardRootRef,
  });

  const setPageSize = (pageSize: number) => {
    setPagination({ pageIndex: 0, pageSize });
  };

  const handleSort = (columnKey: string) => {
    if (!enableSorting) return;

    const column = columns.find((c) => String(c.key) === columnKey);
    if (!column?.sortable) return;

    setSort({
      column: columnKey,
      direction:
        sort.column === columnKey
          ? sort.direction === "asc"
            ? "desc"
            : sort.direction === "desc"
              ? null
              : "asc"
          : "asc",
    });
  };

  const getSortIcon = (columnKey: string) => {
    if (sort.column !== columnKey || !sort.direction) {
      return <ChevronsUpDown className="w-4 h-4 shrink-0 text-muted-foreground/50" />;
    }
    return sort.direction === "asc" ? (
      <ChevronUp className="w-4 h-4 shrink-0 text-foreground" />
    ) : (
      <ChevronDown className="w-4 h-4 shrink-0 text-foreground" />
    );
  };

  // Inline editing handlers
  const startEditing = (rowIndex: number, columnKey: string, currentValue: unknown) => {
    setEditState({
      rowIndex,
      columnKey,
      value: currentValue,
      error: undefined,
    });
  };

  const cancelEditing = () => {
    setEditState(null);
  };

  const saveEdit = async () => {
    if (!editState || !onCellEdit) return;

    const column = columns.find((c) => String(c.key) === editState.columnKey);
    if (column?.editable?.validate) {
      const error = column.editable.validate(editState.value);
      if (error) {
        setEditState({ ...editState, error });
        return;
      }
    }

    setIsSaving(true);
    try {
      const row = paginatedData[editState.rowIndex];
      if (!row) {
        setEditState({ ...editState, error: "Row is no longer available" });
        setIsSaving(false);
        return;
      }
      const dataIndex = sortedData.indexOf(row);
      if (dataIndex === -1) {
        setEditState({ ...editState, error: "Row data changed, please retry" });
        setIsSaving(false);
        return;
      }
      await onCellEdit(dataIndex, editState.columnKey, editState.value, row);
      setEditState(null);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      const errorMessage = error.message || "Failed to save";
      setEditState({ ...editState, error: errorMessage });
      if (onCellEditError) {
        const row = paginatedData[editState.rowIndex];
        if (row) {
          const dataIndex = sortedData.indexOf(row);
          onCellEditError(error, dataIndex, editState.columnKey, editState.value, row);
        }
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveEdit();
    } else if (e.key === "Escape") {
      cancelEditing();
    }
  };

  // Render editable cell
  const renderEditableCell = (
    column: DataTableColumn<T>,
    row: T,
    rowIndex: number,
  ): React.ReactNode => {
    const value = getValue(row, column.key);
    const isEditing =
      editState?.rowIndex === rowIndex && editState?.columnKey === String(column.key);

    if (!column.editable) {
      return column.render ? column.render(value, row, rowIndex) : (value as React.ReactNode);
    }

    if (isEditing) {
      const { type, options, placeholder } = column.editable;

      return (
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {type === "select" && options ? (
            <Select
              value={String(editState.value ?? "")}
              onValueChange={(val) => setEditState({ ...editState, value: val })}
            >
              <SelectTrigger className="h-8 w-full">
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
              <SelectContent>
                {options.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              type={type}
              value={String(editState.value ?? "")}
              onChange={(e) =>
                setEditState({
                  ...editState,
                  value: type === "number" ? Number(e.target.value) : e.target.value,
                  error: undefined,
                })
              }
              onKeyDown={handleEditKeyDown}
              placeholder={placeholder}
              className="h-8 w-full"
              autoFocus
            />
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={saveEdit}
            disabled={isSaving}
          >
            <Check className="h-4 w-4 text-green-600" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={cancelEditing}
            disabled={isSaving}
          >
            <X className="h-4 w-4 text-red-600" />
          </Button>
          {editState.error && (
            <Notice tone="error" appearance="inline" className="ml-1 w-auto text-xs">
              {editState.error}
            </Notice>
          )}
        </div>
      );
    }

    // Non-editing state - show value with click to edit
    return (
      <div
        className="cursor-pointer hover:bg-muted/50 -mx-1 px-1 rounded transition-colors"
        onClick={(e) => {
          e.stopPropagation();
          startEditing(rowIndex, String(column.key), value);
        }}
        title="Click to edit"
      >
        {column.render ? column.render(value, row, rowIndex) : (value as React.ReactNode)}
      </div>
    );
  };

  // Column visibility dropdown component
  const renderColumnVisibilityDropdown = () => {
    if (!showLegacyColumnsToggle) return null;
    return (
      <DataTableColumnVisibilityToggle<T>
        columns={columns}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
      />
    );
  };

  const activeFilterCount = useMemo(() => {
    if (!filters) return 0;
    return filters.filter((filter) => (activeFilterValues[filter.key] ?? []).length > 0).length;
  }, [filters, activeFilterValues]);

  // Cursor pagination controls component
  const renderCursorPaginationControls = () => {
    if (!cursorPagination) return null;
    const { hasNext, hasPrev, onNext, onPrev, isLoading } = cursorPagination;
    // Hide the footer entirely on the very first page when there's nothing more
    // to do — matches the prior hand-rolled behavior.
    if (!hasNext && !hasPrev) return null;
    return (
      <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-border">
        <Button
          variant="outline"
          size="sm"
          className="h-8"
          onClick={onPrev}
          disabled={!hasPrev || isLoading}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-8"
          onClick={onNext}
          disabled={!hasNext || isLoading}
        >
          Next
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    );
  };

  // Pagination controls component
  const renderPaginationControls = () => {
    if (!enablePagination || itemCount === 0) return null;

    const startItem = pagination.pageIndex * pagination.pageSize + 1;
    const endItem = Math.min((pagination.pageIndex + 1) * pagination.pageSize, itemCount);

    return (
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3 border-t border-border">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Rows per page:</span>
          <Select
            value={String(pagination.pageSize)}
            onValueChange={(value) => setPageSize(Number(value))}
          >
            <SelectTrigger className="h-8 w-[70px]" aria-label="Rows per page">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground">
            {startItem}-{endItem} of {itemCount}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => goToPage(pagination.pageIndex - 1)}
              disabled={!canPreviousPage}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => goToPage(pagination.pageIndex + 1)}
              disabled={!canNextPage}
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Render filter UI from filter definitions
  const renderFilters = () => {
    if (!filters || filters.length === 0) return null;

    return filters.map((filter) => {
      const kind = filter.kind ?? "options";
      if (kind === "text" || kind === "number") {
        return (
          <DataTableInputFilter
            key={filter.key}
            filter={filter as DataTableTextFilter | DataTableNumberFilter}
            value={activeFilterValues[filter.key]?.[0] ?? ""}
            onChange={(next) => {
              setFilterValue(filter.key, next === "" ? [] : [next]);
            }}
          />
        );
      }
      if (kind === "toggle") {
        const toggleFilter = filter as DataTableToggleFilter;
        const pressed = (activeFilterValues[toggleFilter.key] ?? []).includes(
          toggleFilter.activeValue,
        );
        return (
          <ToggleFilter
            key={toggleFilter.key}
            label={toggleFilter.label}
            icon={toggleFilter.icon}
            pressed={pressed}
            onPressedChange={(next) => {
              setFilterValue(toggleFilter.key, next ? [toggleFilter.activeValue] : []);
            }}
          />
        );
      }
      const optionsFilter = filter as DataTableOptionsFilter | DataTableSingleFilter;
      const current = activeFilterValues[optionsFilter.key] ?? [];
      return (
        <ComboboxMultiFilter
          key={filter.key}
          label={optionsFilter.label}
          options={optionsFilter.options}
          value={current}
          onValueChange={(values) => {
            // Single-select: keep only the value the user just added, so the
            // control never promises a union the query param cannot express.
            const next =
              kind === "single" ? values.filter((v) => !current.includes(v)).slice(0, 1) : values;
            setFilterValue(optionsFilter.key, next);
          }}
          searchPlaceholder={
            optionsFilter.searchPlaceholder ?? `Filter ${optionsFilter.label.toLowerCase()}…`
          }
        />
      );
    });
  };

  // Table toolbar component
  const renderTableToolbar = () => {
    const hasInlineFilters = !viewControls && Boolean(filters && filters.length > 0);
    const hasViewControls = showDisplayControl || showFilterControl;
    if (!toolbar && !showLegacyColumnsToggle && !hasInlineFilters && !hasViewControls) {
      return null;
    }

    return (
      <div className="flex items-center justify-between gap-2 py-2">
        <div className="flex min-w-0 grow items-center gap-2">
          {hasInlineFilters ? renderFilters() : null}
          {toolbar}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {hasViewControls ? (
            <DataTableViewControls
              columns={columns}
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={setColumnVisibility}
              showDisplay={showDisplayControl}
              showFilter={showFilterControl}
              filterContent={showFilterControl ? renderFilters() : undefined}
              activeFilterCount={activeFilterCount}
            />
          ) : null}
          {showLegacyColumnsToggle ? renderColumnVisibilityDropdown() : null}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div
        ref={setContainerRef}
        className={cn("@container/data-table min-w-0", className)}
        onFocusCapture={claimKeyboardNavigation}
        onPointerDownCapture={claimKeyboardNavigation}
      >
        {renderTableToolbar()}
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  if (data.length === 0 || filteredData.length === 0) {
    const cursorPaginationControls = renderCursorPaginationControls();
    return (
      <div
        ref={setContainerRef}
        className={cn("@container/data-table min-w-0", className)}
        onFocusCapture={claimKeyboardNavigation}
        onPointerDownCapture={claimKeyboardNavigation}
      >
        {renderTableToolbar()}
        {emptyState || (
          <div className="p-8 text-center">
            <div className="text-muted-foreground mb-4">
              <svg
                className="w-16 h-16 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">No data available</h3>
            <p className="text-muted-foreground">There are no items to display at this time.</p>
          </div>
        )}
        {cursorPaginationControls && (
          <div className="mt-4 bg-card rounded-lg shadow-sm border border-border">
            {cursorPaginationControls}
          </div>
        )}
      </div>
    );
  }

  // Narrow-container card view
  if (isCardView) {
    // Find primary column (marked with mobilePrimary or first visible column)
    const primaryColumn =
      visibleColumns.find((c) => c.mobilePrimary) || visibleColumns.find((c) => !c.mobileHidden);
    // Prose-like columns rendered under the card title without their label
    const secondaryColumns = visibleColumns.filter(
      (c) => c.mobileSecondary && !c.mobileHidden && c !== primaryColumn,
    );
    // Remaining visible columns render as label/value pairs
    const mobileVisibleColumns = visibleColumns.filter(
      (c) => !c.mobileHidden && !c.mobileSecondary && c !== primaryColumn,
    );
    const cursorPaginationControls = renderCursorPaginationControls();

    return (
      <div
        ref={setContainerRef}
        className={cn("@container/data-table min-w-0", className)}
        onFocusCapture={claimKeyboardNavigation}
        onPointerDownCapture={claimKeyboardNavigation}
      >
        {renderTableToolbar()}
        <div
          className="space-y-3"
          role={keyboardConfig?.selection ? "grid" : undefined}
          aria-multiselectable={keyboardConfig?.selection ? true : undefined}
          aria-label={keyboardConfig?.selection ? "Table rows" : undefined}
        >
          {paginatedData.map((row, rowIndex) => {
            const key = rowKey(row, rowIndex);
            const isKeyboardActive = keyboardEligible && keyboardActiveIndex === rowIndex;
            const isKeyboardSelected = keyboardConfig?.selection?.selectedRowKeys.has(key);
            const isExpanded = expandedRowKeys.has(key);
            const expandedContentId = `${expansionIdPrefix}-card-row-${rowIndex}-details`;
            return (
              <div
                key={key}
                ref={(node) => {
                  if (node) keyboardRowRefs.current.set(key, node);
                  else keyboardRowRefs.current.delete(key);
                }}
                tabIndex={keyboardEligible ? (isKeyboardActive ? 0 : -1) : undefined}
                role={keyboardConfig?.selection ? "row" : undefined}
                aria-selected={keyboardConfig?.selection ? Boolean(isKeyboardSelected) : undefined}
                data-keyboard-active={isKeyboardActive || undefined}
                data-keyboard-selected={isKeyboardSelected || undefined}
                className={cn(
                  "rounded-lg border border-border bg-card p-4 shadow-sm transition-colors duration-200 outline-none",
                  (onRowClick || renderExpandedRow) &&
                    !editState &&
                    "cursor-pointer hover:bg-muted/50 active:bg-muted",
                  isKeyboardSelected && "bg-muted/40",
                  isKeyboardActive && "ring-2 ring-ring ring-offset-1 ring-offset-background",
                  rowClassName?.(row, rowIndex),
                )}
                onClick={(event) => {
                  if (editState) return;
                  if (onRowClick) onRowClick(row, rowIndex, event);
                  else if (renderExpandedRow) toggleExpandedRow(key);
                }}
                onFocus={() => keyboardEligible && setKeyboardActiveRowKey(key)}
              >
                <div className="flex min-w-0 items-start gap-2">
                  {renderExpandedRow && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="-ml-2 -mt-2 size-8 text-muted-foreground focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                      aria-label={`${isExpanded ? "Collapse" : "Expand"} row ${rowIndex + 1}`}
                      aria-expanded={isExpanded}
                      aria-controls={expandedContentId}
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleExpandedRow(key);
                      }}
                    >
                      {isExpanded ? <ChevronDown /> : <ChevronRight />}
                    </Button>
                  )}
                  <div className="min-w-0 flex-1">
                    {/* Primary field as card title, prose columns as body text */}
                    {(primaryColumn || secondaryColumns.length > 0) && (
                      <div
                        className="mb-3 space-y-1.5"
                        role={keyboardConfig?.selection ? "gridcell" : undefined}
                      >
                        {primaryColumn && (
                          <div className="min-w-0 font-medium text-foreground">
                            {hasEditableColumns && primaryColumn.editable
                              ? renderEditableCell(primaryColumn, row, rowIndex)
                              : renderCell(primaryColumn, row, rowIndex)}
                          </div>
                        )}
                        {secondaryColumns.map((column, colIndex) => (
                          <div key={colIndex} className="min-w-0 text-sm text-muted-foreground">
                            {renderCell(column, row, rowIndex)}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Other fields as label-value pairs */}
                    {mobileVisibleColumns.length > 0 && (
                      <div
                        className="space-y-2 text-sm"
                        role={keyboardConfig?.selection ? "gridcell" : undefined}
                      >
                        {mobileVisibleColumns.map((column, colIndex) => (
                          <div key={colIndex} className="flex justify-between items-start gap-2">
                            {column.label && (
                              <span className="text-muted-foreground shrink-0">{column.label}</span>
                            )}
                            <span className="min-w-0 flex-1 text-right text-foreground">
                              {hasEditableColumns && column.editable
                                ? renderEditableCell(column, row, rowIndex)
                                : renderCell(column, row, rowIndex)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                {renderExpandedRow && isExpanded && (
                  <div
                    id={expandedContentId}
                    role="region"
                    aria-label={`Details for row ${rowIndex + 1}`}
                    className="mt-4 min-w-0 border-t border-border pt-4"
                    onClick={(event) => {
                      // Detail controls must remain usable without collapsing their parent card.
                      event.stopPropagation();
                    }}
                  >
                    {renderExpandedRow(row, rowIndex)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {enablePagination && (
          <div className="mt-4 bg-card rounded-lg shadow-sm border border-border">
            {renderPaginationControls()}
          </div>
        )}
        {cursorPaginationControls && (
          <div className="mt-4 bg-card rounded-lg shadow-sm border border-border">
            {cursorPaginationControls}
          </div>
        )}
      </div>
    );
  }

  // Table view
  return (
    <div
      ref={setContainerRef}
      className={cn("@container/data-table min-w-0", className)}
      onFocusCapture={claimKeyboardNavigation}
      onPointerDownCapture={claimKeyboardNavigation}
    >
      {renderTableToolbar()}
      <div className="rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table
            className={cn("w-full table-fixed divide-y divide-border", tableClassName)}
            style={
              effectiveTableMinWidth > 0 ? { minWidth: `${effectiveTableMinWidth}px` } : undefined
            }
            role={keyboardConfig?.selection ? "grid" : undefined}
            aria-multiselectable={keyboardConfig?.selection ? true : undefined}
          >
            <thead className={hideHeader ? "sr-only" : undefined}>
              <tr className="border-b border-border bg-muted/50">
                {renderExpandedRow && (
                  <th scope="col" className="w-10 px-1 py-3">
                    <span className="sr-only">Expand row</span>
                  </th>
                )}
                {visibleColumns.map((column, index) => {
                  const isSortable = enableSorting && column.sortable;
                  // The label sits inside a flex container, which ignores
                  // the `<th>`'s `text-align`. Mirror the column's text
                  // alignment onto the flex container so right-aligned
                  // numeric columns (e.g. "Confidence") have their header
                  // and values share a baseline. Default is start/left.
                  const headerJustify = column.className?.includes("text-right")
                    ? "justify-end"
                    : column.className?.includes("text-center")
                      ? "justify-center"
                      : null;
                  return (
                    <th
                      key={index}
                      scope="col"
                      className={cn(
                        "px-4 py-3 text-left font-medium text-foreground",
                        isSortable && "cursor-pointer select-none hover:bg-muted/80",
                        column.className,
                      )}
                      style={columnStyles[index]}
                      onClick={() => isSortable && !editState && handleSort(String(column.key))}
                    >
                      <div className={cn("flex items-center space-x-1 min-w-0", headerJustify)}>
                        {column.labelHidden ? (
                          <span className="sr-only">{column.label}</span>
                        ) : (
                          <TruncatedValue>{column.label}</TruncatedValue>
                        )}
                        {isSortable && getSortIcon(String(column.key))}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((row, rowIndex) => {
                const key = rowKey(row, rowIndex);
                const isKeyboardActive = keyboardEligible && keyboardActiveIndex === rowIndex;
                const isKeyboardSelected = keyboardConfig?.selection?.selectedRowKeys.has(key);
                const isExpanded = expandedRowKeys.has(key);
                const expandedContentId = `${expansionIdPrefix}-table-row-${rowIndex}-details`;
                return (
                  <React.Fragment key={key}>
                    <tr
                      ref={(node) => {
                        if (node) keyboardRowRefs.current.set(key, node);
                        else keyboardRowRefs.current.delete(key);
                      }}
                      tabIndex={keyboardEligible ? (isKeyboardActive ? 0 : -1) : undefined}
                      aria-selected={
                        keyboardConfig?.selection ? Boolean(isKeyboardSelected) : undefined
                      }
                      data-keyboard-active={isKeyboardActive || undefined}
                      data-keyboard-selected={isKeyboardSelected || undefined}
                      className={cn(
                        "border-b border-border outline-none last:border-0 hover:bg-muted/30",
                        (onRowClick || renderExpandedRow) && !editState && "cursor-pointer",
                        isKeyboardSelected && "bg-muted/40",
                        isKeyboardActive && "ring-2 ring-inset ring-ring",
                        rowClassName?.(row, rowIndex),
                      )}
                      onClick={(event) => {
                        if (editState) return;
                        if (onRowClick) onRowClick(row, rowIndex, event);
                        else if (renderExpandedRow) toggleExpandedRow(key);
                      }}
                      onFocus={() => keyboardEligible && setKeyboardActiveRowKey(key)}
                    >
                      {renderExpandedRow && (
                        <td className="w-10 px-1 py-2 align-middle">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="size-8 text-muted-foreground focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                            aria-label={`${isExpanded ? "Collapse" : "Expand"} row ${rowIndex + 1}`}
                            aria-expanded={isExpanded}
                            aria-controls={expandedContentId}
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleExpandedRow(key);
                            }}
                          >
                            {isExpanded ? <ChevronDown /> : <ChevronRight />}
                          </Button>
                        </td>
                      )}
                      {visibleColumns.map((column, colIndex) => {
                        const content =
                          hasEditableColumns && column.editable
                            ? renderEditableCell(column, row, rowIndex)
                            : renderCell(column, row, rowIndex);
                        const wrapped =
                          !column.nowrap && typeof content === "string" ? (
                            <TruncatedValue>{content}</TruncatedValue>
                          ) : (
                            content
                          );
                        return (
                          <td
                            key={colIndex}
                            className={cn(
                              "px-4 py-3 text-sm text-foreground align-middle",
                              column.nowrap ? "whitespace-nowrap" : "overflow-hidden text-ellipsis",
                              column.className,
                            )}
                            style={columnStyles[colIndex]}
                          >
                            {wrapped}
                          </td>
                        );
                      })}
                    </tr>
                    {renderExpandedRow && isExpanded && (
                      <tr className="border-b border-border last:border-0">
                        <td colSpan={visibleColumns.length + 1} className="p-0 align-top">
                          <div
                            id={expandedContentId}
                            role="region"
                            aria-label={`Details for row ${rowIndex + 1}`}
                            className="min-w-0 bg-muted/20 px-4 py-3"
                          >
                            {renderExpandedRow(row, rowIndex)}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {renderPaginationControls()}
        {renderCursorPaginationControls()}
      </div>
      {footer && <div className="py-2 text-sm text-muted-foreground">{footer}</div>}
    </div>
  );
}

/**
 * Internal: renders a text or number filter as a debounced `<Input>` inside
 * the DataTable toolbar. Keeps draft state local so we don't refetch on every
 * keystroke when `debounceMs` is set.
 */
function DataTableInputFilter({
  filter,
  value,
  onChange,
}: {
  filter: DataTableTextFilter | DataTableNumberFilter;
  value: string;
  onChange: (next: string) => void;
}) {
  const [draft, setDraft] = useState(value);

  // Reset draft when the controlled value changes from outside (e.g. clear all).
  useEffect(() => {
    setDraft(value);
  }, [value]);

  // Debounced emit. Skips the timer when debounceMs is 0/undefined.
  useEffect(() => {
    if (draft === value) return;
    const delay = filter.debounceMs ?? 0;
    if (delay === 0) {
      onChange(draft);
      return;
    }
    const t = setTimeout(() => onChange(draft), delay);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  const defaultWidth = filter.kind === "number" ? "w-[100px]" : "w-[200px]";

  return (
    <Input
      type={filter.kind === "number" ? "number" : "text"}
      inputMode={filter.kind === "number" ? "numeric" : undefined}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      placeholder={filter.placeholder ?? filter.label}
      className={cn("h-9 font-mono text-xs", defaultWidth, filter.inputClassName)}
      aria-label={filter.label}
    />
  );
}

// Helper components for common cell types
export const DataTableBadge = ({
  value,
  variant = "secondary",
}: {
  value: string;
  variant?: "default" | "secondary" | "destructive" | "outline";
}) => <Badge variant={variant}>{value}</Badge>;

export const DataTableDate = ({
  value,
  format = "short",
}: {
  value: string | Date;
  format?: "short" | "long" | "time" | "relative";
}) => {
  const date = new Date(value);

  if (format === "relative") {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    let relativeText: string;
    if (diffSecs < 60) {
      relativeText = "just now";
    } else if (diffMins < 60) {
      relativeText = `${diffMins}m ago`;
    } else if (diffHours < 24) {
      relativeText = `${diffHours}h ago`;
    } else if (diffDays < 7) {
      relativeText = `${diffDays}d ago`;
    } else {
      relativeText = date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    }
    return <span>{relativeText}</span>;
  }

  const formatOptions: Intl.DateTimeFormatOptions =
    format === "short"
      ? { year: "numeric", month: "short", day: "numeric" }
      : format === "long"
        ? { year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" }
        : { hour: "2-digit", minute: "2-digit" };

  return <span>{date.toLocaleDateString("en-US", formatOptions)}</span>;
};

export const DataTableLink = ({
  href,
  children,
  className = "text-primary hover:text-primary/80 hover:underline transition-colors duration-200",
  search,
}: {
  href: string;
  children: React.ReactNode;
  className?: string;
  /**
   * Search-params override for router-aware links. TanStack Router preserves
   * the current query string when omitted, so links from a filtered list to a
   * detail page pass `search={{}}` to keep the filters off the detail URL.
   * Ignored by the plain `<a>` fallback (anchors never inherit the query).
   */
  search?: Record<string, unknown>;
}) => {
  // Prefer the router-aware `<Link>` from `LinkProvider` so clicks
  // stay inside the SPA (no full page reload), falling back to a
  // plain `<a>` when the provider isn't wired up (e.g. Storybook).
  // `stopPropagation` still runs so a row-level click handler on
  // the parent `<tr>` doesn't double-fire with the link click.
  const LinkComponent = useLinkComponent();
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };
  if (LinkComponent) {
    return (
      <LinkComponent
        to={href}
        className={className}
        onClick={handleClick}
        {...(search !== undefined ? { search } : {})}
      >
        {children}
      </LinkComponent>
    );
  }
  return (
    <a href={href} className={className} onClick={handleClick}>
      {children}
    </a>
  );
};
